import { AGG_WORDS, BUCKETS, fixedLocale, TIME_PHRASES } from './lexicon'
import { fold } from './text'
import type { Bucket, CatalogSet, Definition, Filter, Locale, Measure, PeriodKey, ResultColumn, ValueIndex, View, Words } from './types'

/**
 * Definitia spusa inapoi in cuvinte, din bucati clicabile: *Numarul de*
 * [tichete] [deschise] *pe* [sediu] *in* [luna trecuta], *doar* [Bucuresti].
 * Fiecare bucata stie sa se scoata din definitie; perioada, gruparea, socoteala
 * si vederea au si variante de schimbat.
 */

export type ChipKind = 'measure' | 'dataset' | 'filter' | 'restrict' | 'group' | 'period' | 'view'

export interface Chip {
  kind: ChipKind
  text: string
  /** Cuvantul de legatura dinaintea bucatii ("pe", "in", "doar"). */
  lead: string
  /** Definitia fara bucata asta; null = nu se poate scoate (setul). */
  remove: (() => Definition) | null
}

export const PERIOD_LABELS: Record<PeriodKey, Words> = {
  all: { ro: 'oricand', en: 'any time' },
  today: { ro: 'azi', en: 'today' },
  yesterday: { ro: 'ieri', en: 'yesterday' },
  this_week: { ro: 'saptamana aceasta', en: 'this week' },
  last_week: { ro: 'saptamana trecuta', en: 'last week' },
  this_month: { ro: 'luna aceasta', en: 'this month' },
  last_month: { ro: 'luna trecuta', en: 'last month' },
  this_quarter: { ro: 'trimestrul acesta', en: 'this quarter' },
  last_quarter: { ro: 'trimestrul trecut', en: 'last quarter' },
  this_year: { ro: 'anul acesta', en: 'this year' },
  last_year: { ro: 'anul trecut', en: 'last year' },
  last_7_days: { ro: 'ultimele 7 zile', en: 'last 7 days' },
  last_30_days: { ro: 'ultimele 30 de zile', en: 'last 30 days' },
  last_90_days: { ro: 'ultimele 90 de zile', en: 'last 90 days' },
  last_n_days: { ro: 'ultimele N zile', en: 'last N days' },
  custom: { ro: 'interval', en: 'range' },
  next_7_days: { ro: 'urmatoarele 7 zile', en: 'next 7 days' },
  next_30_days: { ro: 'urmatoarele 30 de zile', en: 'next 30 days' },
  next_90_days: { ro: 'urmatoarele 90 de zile', en: 'next 90 days' },
  next_month: { ro: 'luna viitoare', en: 'next month' },
}

export const AGG_LABELS: Record<Measure['agg'], Words> = {
  count: { ro: 'Numarul de', en: 'Number of' },
  count_distinct: { ro: 'Numarul distinct de', en: 'Distinct number of' },
  sum: { ro: 'Suma', en: 'Sum of' },
  avg: { ro: 'Media', en: 'Average' },
  min: { ro: 'Minimul', en: 'Minimum' },
  max: { ro: 'Maximul', en: 'Maximum' },
  ratio: { ro: 'Procentul', en: 'Percentage' },
}

export const BUCKET_LABELS: Record<Bucket, Words> = {
  day: { ro: 'zi', en: 'day' },
  week: { ro: 'saptamana', en: 'week' },
  month: { ro: 'luna', en: 'month' },
  quarter: { ro: 'trimestru', en: 'quarter' },
  year: { ro: 'an', en: 'year' },
}

export const VIEW_LABELS: Record<View, Words> = {
  kpi: { ro: 'cifra', en: 'number' },
  table: { ro: 'tabel', en: 'table' },
  bar: { ro: 'bare', en: 'bars' },
  line: { ro: 'linie', en: 'line' },
  topn: { ro: 'top', en: 'top' },
  pie: { ro: 'inel', en: 'donut' },
  stacked: { ro: 'bare stivuite', en: 'stacked bars' },
  area: { ro: 'suprafata', en: 'area' },
  pivot: { ro: 'tabel incrucisat', en: 'pivot / heatmap' },
  gauge: { ro: 'indicator cu tinta', en: 'gauge with target' },
}

/** Toate vederile, in ordinea din liste (constructor, bucata de vedere, bloc). */
export const ALL_VIEWS: View[] = ['table', 'kpi', 'gauge', 'bar', 'stacked', 'line', 'area', 'topn', 'pie', 'pivot']

/** Cuvantul randurilor de jos, ca `Formatter::FOOT`. */
export const FOOT_LABELS: Record<'others' | 'total', Words> = {
  others: { ro: 'Altele', en: 'Others' },
  total: { ro: 'Total', en: 'Total' },
}

const OP_WORDS: Record<string, Words> = {
  eq: { ro: '', en: '' },
  neq: { ro: 'nu ', en: 'not ' },
  in: { ro: '', en: '' },
  nin: { ro: 'fara ', en: 'without ' },
  contains: { ro: 'contine ', en: 'contains ' },
  ncontains: { ro: 'nu contine ', en: 'does not contain ' },
  starts: { ro: 'incepe cu ', en: 'starts with ' },
  gt: { ro: '> ', en: '> ' },
  gte: { ro: '>= ', en: '>= ' },
  lt: { ro: '< ', en: '< ' },
  lte: { ro: '<= ', en: '<= ' },
  between: { ro: 'intre ', en: 'between ' },
  empty: { ro: 'gol', en: 'empty' },
  nempty: { ro: 'completat', en: 'filled in' },
}

export interface Names {
  values: ValueIndex
  offices: { id: number; name: string }[]
}

function valueName(names: Names, dataset: string, field: string, id: number | string, set?: CatalogSet, locale: Locale = 'ro'): string {
  if (field === 'office') {
    const office = names.offices.find((o) => o.id === id)
    if (office) return office.name
  }
  /* Codurile (stare, risc, gravitate) isi au eticheta in catalog. */
  const option = set?.fields.find((f) => f.key === field)?.options?.find((o) => String(o.value) === String(id))
  if (option) return option.label[locale]
  const item = names.values[dataset]?.[field]?.find((v) => v.id === id)
  return item ? item.label : `#${id}`
}

function filterText(set: CatalogSet, f: Filter, locale: Locale, names: Names): string {
  const field = set.fields.find((x) => x.key === f.field)
  const label = field ? field.label[locale] : f.field
  if (field?.type === 'bool') {
    const yes = (f.op === 'eq') === (f.value === 1)
    return yes ? label.toLowerCase() : `${locale === 'ro' ? 'fara' : 'not'} ${label.toLowerCase()}`
  }
  const op = OP_WORDS[f.op]?.[locale] ?? ''
  const raw = Array.isArray(f.value) ? f.value : f.value === undefined ? [] : [f.value]
  const shown = raw.map((v) => (field?.type === 'enum' && typeof v === 'number' ? valueName(names, set.key, f.field, v, set, locale) : String(v)))
  return `${label}: ${op}${shown.join(locale === 'ro' ? ' sau ' : ' or ')}`.trim()
}

export function chips(def: Definition, catalog: CatalogSet[], locale: Locale, names: Names): Chip[] {
  const set = catalog.find((s) => s.key === def.dataset)
  if (!set) return []
  const out: Chip[] = []
  const field = (key: string | undefined) => set.fields.find((f) => f.key === key)
  const without = (patch: Partial<Definition>): Definition => {
    const next: Definition = { ...def, ...patch }
    for (const [k, v] of Object.entries(patch)) if (v === undefined) delete (next as unknown as Record<string, unknown>)[k]
    return next
  }

  for (const m of def.measures ?? []) {
    const f = field(m.field)
    const text = AGG_LABELS[m.agg][locale] + (f ? ` ${f.label[locale].toLowerCase()}` : '')
    out.push({ kind: 'measure', text, lead: '', remove: () => without({ measures: (def.measures ?? []).filter((x) => x !== m), view: undefined }) })
  }
  out.push({ kind: 'dataset', text: set.label[locale].toLowerCase(), lead: '', remove: null })
  ;(def.filters ?? []).forEach((f, i) => {
    /* Filtrele "oricare" se leaga cu "sau"; cand ramane unul, legatura nu mai conteaza. */
    const lead = def.match === 'any' && i > 0 ? (locale === 'ro' ? 'sau' : 'or') : ''
    out.push({
      kind: 'filter',
      text: filterText(set, f, locale, names),
      lead,
      remove: () => {
        const rest = (def.filters ?? []).filter((x) => x !== f)
        return without({ filters: rest.length > 0 ? rest : undefined, ...(rest.length < 2 ? { match: undefined } : {}) })
      },
    })
  })
  for (const g of def.group_by ?? []) {
    const f = field(g.field)
    const text = g.bucket ? BUCKET_LABELS[g.bucket][locale] : (f?.label[locale] ?? g.field).toLowerCase()
    out.push({
      kind: 'group',
      text,
      lead: locale === 'ro' ? 'pe' : 'by',
      remove: () => {
        const rest = (def.group_by ?? []).filter((x) => x !== g)
        return without({ group_by: rest.length > 0 ? rest : undefined, view: undefined, sort: undefined, limit: undefined })
      },
    })
  }
  if (def.period && def.period.key !== 'all') {
    const p = def.period
    const text = p.key === 'custom' ? `${p.from} - ${p.to}` : p.key === 'last_n_days' ? (locale === 'ro' ? `ultimele ${p.n} zile` : `last ${p.n} days`) : PERIOD_LABELS[p.key][locale]
    /* Perioada pe alt camp decat cel obisnuit se spune: "adaugat in ultimele 30 de zile". */
    const on = def.time_field && def.time_field !== set.time ? field(def.time_field)?.label[locale].toLowerCase() : undefined
    out.push({ kind: 'period', text, lead: on ? `${on} in` : 'in', remove: () => without({ period: undefined, time_field: undefined }) })
  }
  for (const [dim, ids] of Object.entries(def.restrict ?? {}) as [keyof NonNullable<Definition['restrict']>, number[]][]) {
    const key = dim === 'office' ? 'office' : dim === 'person' ? 'assignee' : dim === 'group' ? 'group' : 'device'
    const label = field(key)?.label[locale] ?? dim
    const text = `${label}: ${ids.map((id) => valueName(names, set.key, key, id)).join(locale === 'ro' ? ' sau ' : ' or ')}`
    out.push({
      kind: 'restrict',
      text,
      lead: locale === 'ro' ? 'doar' : 'only',
      remove: () => {
        const restrict = { ...(def.restrict ?? {}) }
        delete restrict[dim]
        return without({ restrict: Object.keys(restrict).length > 0 ? restrict : undefined })
      },
    })
  }
  return out
}

/* ========================================================================= */

/**
 * Completarea din mers: ultimul cuvant inceput, potrivit la inceputul
 * cuvintelor din vocabular (seturi, campuri, valori, timp). Se intoarce
 * fraza intreaga, cu ultimul cuvant inlocuit.
 */
export function complete(text: string, catalog: CatalogSet[], values: ValueIndex, locale: Locale, max = 8): string[] {
  const m = /(\S+)$/.exec(text)
  if (!m || text.endsWith(' ')) return []
  const partial = fold(m[1]!)
  if (partial.length < 2) return []
  const head = text.slice(0, text.length - m[1]!.length)
  const pool = new Set<string>()
  for (const s of catalog) {
    pool.add(s.label[locale].toLowerCase())
    for (const w of s.syn[locale]) pool.add(w)
    for (const f of s.fields) {
      pool.add(f.label[locale].toLowerCase())
      for (const w of f.syn[locale]) pool.add(w)
    }
    for (const list of Object.values(values[s.key] ?? {})) for (const v of list) pool.add(v.label)
  }
  /* Cuvintele fixe, numai in limba aleasa. */
  const mine = (w: string) => fixedLocale(w) === 'both' || fixedLocale(w) === locale
  for (const [w] of TIME_PHRASES) if (mine(w)) pool.add(w)
  for (const [w] of AGG_WORDS) if (w.length > 2 && mine(w)) pool.add(w)
  for (const [w] of BUCKETS) if (mine(w)) pool.add(w)
  const hits = [...pool].filter((w) => fold(w).startsWith(partial) && fold(w) !== partial)
  hits.sort((a, b) => a.length - b.length || a.localeCompare(b))
  return hits.slice(0, max).map((w) => head + w)
}

/* ========================================================================= */

/** O celula, cum se arata pe ecran. Aceleasi reguli ca `Formatter::cell` din motor. */
export function cellText(value: string | number | null, column: ResultColumn, locale: Locale): string {
  if (value === null || value === '') return ''
  if (value === '***') return '***'
  if (column.map && column.map[String(value)]) return column.map[String(value)]![locale]
  if (column.type === 'duration') return duration(Number(value))
  if (column.type === 'bytes') return bytes(Number(value))
  if (column.type === 'percent') return `${Number(value).toFixed(1)}%`
  return String(value)
}

export function duration(seconds: number): string {
  const sign = seconds < 0 ? '-' : ''
  const s = Math.round(Math.abs(seconds))
  const h = Math.floor(s / 3600)
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${sign}${h}:${mm}:${ss}`
}

export function bytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return i === 0 ? `${Math.trunc(n)} B` : `${n.toFixed(1)} ${units[i]}`
}

export function columnTitle(column: ResultColumn, locale: Locale): string {
  if (column.title) return column.title
  if (column.role === 'measure') {
    const agg = (column.agg ?? 'count') as Measure['agg']
    return column.field && AGG_LABELS[agg] ? `${AGG_LABELS[agg][locale]} ${column.label[locale].toLowerCase()}` : column.label[locale]
  }
  return column.label[locale]
}

/** Schimbarea fata de perioada dinainte: sageata si procentul; null = nu se poate spune. */
export function change(now: number | null, before: number | null): { arrow: '▲' | '▼' | '='; pct: number | null } | null {
  if (now === null || before === null) return null
  if (now === before) return { arrow: '=', pct: 0 }
  const arrow = now > before ? '▲' : '▼'
  return { arrow, pct: before === 0 ? null : Math.round(((now - before) / Math.abs(before)) * 1000) / 10 }
}

/**
 * Filtrele inca necompletate nu pleaca la server: un rand gol din constructor,
 * sau casuta de cautare a unui bloc din sablon ("Imprimanta contine ...")
 * lasata goala. Tot asa se salveaza si se descarca.
 */
export function sanitize(def: Definition): Definition {
  const filters = (def.filters ?? []).filter((f) => {
    if (f.op === 'empty' || f.op === 'nempty') return true
    /* "Intre" cere ambele capete. */
    if (f.op === 'between') return Array.isArray(f.value) && f.value.length === 2 && f.value.every((v) => v !== '' && v !== null && v !== undefined)
    if (Array.isArray(f.value)) return f.value.length > 0
    return f.value !== undefined && f.value !== ''
  })
  const out: Definition = { ...def }
  if (filters.length > 0) out.filters = filters
  else delete out.filters
  return out
}
